/**
 * The ecosystem as a fishbone (owner, 2026-09-26: "show tree or fishbone type of digram?").
 *
 * WHY A FISHBONE AND NOT A ROW OF BOXES. The six groups are not six steps. Four of them lie on the
 * path the material itself takes - plantation, intermediaries, processing, buyers - while inputs
 * and services never touch that path: they FEED the plantation and stop. Six boxes in a row said
 * they were all the same kind of thing, and at 1280px the grid wrapped and left arrows pointing at
 * nothing. A spine with bones says which is which, and cannot wrap.
 *
 * WHY SVG. A CSS grid reflows on the reader's width, which is exactly what broke the first
 * attempt. A viewBox scales instead of reflowing, so the diagram is the same diagram on a phone as
 * on a desk - smaller, not rearranged.
 *
 * WHAT IT IS FOR. A list answers "who is in my chain". Only a drawing answers "where is the gap":
 * no haulier between the plantation and the mill means either the mill collects, or nobody has
 * recorded who carries the timber - and for a chain of custody those are very different. Empty
 * stages are therefore drawn dashed and labelled in words, not left as a zero the eye slides past.
 *
 * ORDER COMES FROM THE DATABASE (`party.fn_group_order`); this file holds geometry and labels.
 */
import { Fragment } from 'react';

export interface EcosystemGroup {
  chain_group: string;
  group_order: number;
  entity_count: number;
  needs_attention: number;
}

// Three lines, not two. The + and the step count were laid over the blurb when the box was 66
// tall - "Members and plots+" and "none recordedh ste" - so the box now has a row of its own for
// the model, under the row that describes the stage. Wider too: "Intermediaries" and "2 to chase"
// together need more than 168px and were overlapping at the margin.
const W = 184;
const H = 84;
const GAP = 56;          // room for an arrow between two spine nodes
const ROW = 168;         // the spine's row

/** Where each group sits. Feeders above, the spine below, in flow order left to right. */
const PLACE: Record<string, { x: number; y: number; label: string; blurb: string; feeder?: boolean }> = {
  inputs:         { x: 24,                    y: 14,  label: 'Inputs',         blurb: 'Seedlings, materials', feeder: true },
  services:       { x: 24 + W + GAP,          y: 14,  label: 'Services',       blurb: 'Labour, machinery',    feeder: true },
  plantation:     { x: 24,                    y: ROW, label: 'Plantation',     blurb: 'Members and plots' },
  intermediaries: { x: 24 + (W + GAP),        y: ROW, label: 'Intermediaries', blurb: 'Hauliers, agents' },
  processing:     { x: 24 + 2 * (W + GAP),    y: ROW, label: 'Processing',     blurb: 'Mills, seasoning' },
  buyers:         { x: 24 + 3 * (W + GAP),    y: ROW, label: 'Buyers',         blurb: 'Buyers, exporters' },
};

const SPINE_Y = ROW + H / 2;

function Node({
  group, active, steps, onSelect,
}: {
  group: EcosystemGroup;
  active: boolean;
  steps: number;
  onSelect: (key: string | null) => void;
}) {
  const at = PLACE[group.chain_group];
  if (!at) return null;
  const empty = group.entity_count === 0;

  return (
    <g
      aria-pressed={active}
      className={`eco-node${active ? ' is-active' : ''}${empty ? ' is-empty' : ''}`}
      // Clicking the active stage clears it, so the chart is a filter you can put down.
      onClick={() => onSelect(active ? null : group.chain_group)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(active ? null : group.chain_group);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <title>{`${at.label}: ${group.entity_count} recorded`}</title>
      <rect className="eco-node-box" height={H} rx="10" width={W} x={at.x} y={at.y} />
      <text className="eco-node-label" x={at.x + 13} y={at.y + 21}>{at.label}</text>
      <text className="eco-node-count" x={at.x + 13} y={at.y + 50}>{group.entity_count}</text>
      {/* "none recorded" REPLACES the blurb rather than sitting beside the label. Right-aligned on
          the label's line it collided with "Intermediaries" and "Processing" - 168px cannot hold
          both - and the blurb line is free precisely when there is nothing to describe. */}
      <text className={empty ? 'eco-node-gap' : 'eco-node-blurb'} x={at.x + 47} y={at.y + 49}>
        {empty ? 'none recorded' : at.blurb}
      </text>
      {/* Only ever on a stage that HAS entities, so it never shares a line with the above. */}
      {group.needs_attention > 0 && (
        <text className="eco-node-chase" x={at.x + W - 13} y={at.y + 21}>
          {group.needs_attention} to chase
        </text>
      )}
      {/* How many steps of the traceability MODEL are defined at this stage. Distinct from the
          entity count above it: one names who is here, the other what is meant to happen. */}
      {steps > 0 && (
        <text className="eco-node-steps" x={at.x + 13} y={at.y + 72}>
          {steps} step{steps === 1 ? '' : 's'} defined
        </text>
      )}
    </g>
  );
}

export function EcosystemFlow({
  groups, selected, onSelect, stepCounts = {}, onAddStep,
}: {
  groups: EcosystemGroup[];
  selected: string | null;
  onSelect: (key: string | null) => void;
  /** Steps of the traceability model defined at each stage, keyed by group. */
  stepCounts?: Record<string, number>;
  /** When given, each stage shows a + that opens the step editor. */
  onAddStep?: (key: string) => void;
}) {
  const byKey = new Map(groups.map((g) => [g.chain_group, g]));
  const spine = ['plantation', 'intermediaries', 'processing', 'buyers'];

  return (
    <section aria-label="The ecosystem, as a flow" className="card eco-flow">
      <div className="section-heading">
        <h3>How material moves</h3>
        <span className="muted">Choose a stage to see who is in it</span>
      </div>

      <svg className="eco-svg" preserveAspectRatio="xMidYMid meet" role="img" viewBox="0 0 976 282">
        <defs>
          <marker id="eco-head" markerHeight="7" markerWidth="7" orient="auto"
                  refX="6" refY="3.5">
            <polygon className="eco-arrowhead" points="0 0, 7 3.5, 0 7" />
          </marker>
        </defs>

        {/* The spine: the path the material itself takes. */}
        {spine.slice(0, -1).map((key, index) => {
          const from = PLACE[key];
          const to = PLACE[spine[index + 1] as string];
          if (!from || !to) return null;
          return (
            <line
              className="eco-spine"
              key={key}
              markerEnd="url(#eco-head)"
              x1={from.x + W}
              x2={to.x - 8}
              y1={SPINE_Y}
              y2={SPINE_Y}
            />
          );
        })}

        {/* The bones: what feeds the plantation without ever joining the chain. Drawn to the TOP
            edge of the plantation box, so they visibly stop there rather than passing through. */}
        {['inputs', 'services'].map((key) => {
          const from = PLACE[key];
          const plantation = PLACE.plantation;
          if (!from || !plantation) return null;
          return (
            // No arrowhead (owner, 2026-09-26). An arrow says "material moves this way", and that
            // is the spine's claim to make: inputs and services SUPPLY the plantation, they are
            // not a leg of the chain of custody. The dashed line still shows the relationship.
            <line
              className="eco-bone"
              key={key}
              x1={from.x + W / 2}
              x2={plantation.x + W / 2}
              y1={from.y + H}
              y2={plantation.y - 8}
            />
          );
        })}

        {Object.keys(PLACE).map((key) => {
          const group = byKey.get(key);
          const at = PLACE[key];
          if (!group || !at) return null;
          return (
            <Fragment key={key}>
              <Node
                active={selected === key}
                group={group}
                onSelect={onSelect}
                steps={stepCounts[key] ?? 0} />
              {/* The + sits OUTSIDE the node's own click target, because adding a step and
                  filtering the list below are different intentions and a button inside a button
                  is ambiguous to a mouse and impossible for a keyboard. */}
              {onAddStep && (
                <g
                  className="eco-add"
                  onClick={(event) => { event.stopPropagation(); onAddStep(key); }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onAddStep(key);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <title>{`Add a step at ${at.label}`}</title>
                  <circle cx={at.x + W - 18} cy={at.y + H - 18} r="11" />
                  <text x={at.x + W - 18} y={at.y + H - 13}>+</text>
                </g>
              )}
            </Fragment>
          );
        })}
      </svg>

      <p className="muted eco-flow-note">
        Inputs and services feed the plantation; the spine is the path the timber itself takes. An
        actor working at two stages is counted at both, so the stages do not add up to the total.
      </p>
    </section>
  );
}
